import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { createCourse, CourseInput, getMyCourses, updateCourse } from '../api/courses';
import {
  CONTENT_LIMITS,
  COURSE_LANGUAGE_OPTIONS,
  COURSE_LIMITS,
  CourseLanguage,
  DURATION_MODE_OPTIONS,
  DurationMode,
  GRADE_LEVELS,
  GRADE_LEVEL_LABELS,
  OFFER_MODE_OPTIONS,
  OfferMode,
  SCHEDULE_LIMITS,
  SCHEDULE_TYPE_OPTIONS,
  ScheduleType,
  SUBJECTS,
  WEEK_DAYS,
} from '../domain/catalog';
import { PLATFORM_CURRENCY } from '../../../commodity/format/money';
import type { LearningProfile } from '../api/learning';
import {
  LEARNING_PROFILE_NOTE,
  LEARNING_PROFILE_OPTIONS,
  MVP_PROFILES,
} from '../vocabulary/learningVocabulary';

interface FormState {
  title: string;
  description: string;
  subject: string;
  gradeLevel: string[];
  duration: string;
  maxStudents: string;
  price: string;
  offerMode: OfferMode;
  scheduleType: ScheduleType;
  classDays: number[];
  startTime: string;
  endTime: string;
  timezone: string;
  durationMode: DurationMode;
  durationWeeks: string;
  /** LEM-01. Unset on a new course: the teacher states the promise. */
  learningProfile: LearningProfile | '';
  // ── Course content (ADR-007 S4.3)
  language: CourseLanguage | '';
  overview: string;
  learningObjectives: string[];
  classStructure: string;
  whatToExpect: string;
}

type Errors = Partial<Record<keyof FormState, string>>;

/** Best-effort guess so the teacher rarely has to think about it. */
const localTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Mexico_City';
  } catch {
    return 'America/Mexico_City';
  }
};

const EMPTY: FormState = {
  title: '',
  description: '',
  subject: SUBJECTS[0],
  gradeLevel: [],
  duration: '60',
  maxStudents: '10',
  price: '25',
  offerMode: 'GROUP',
  // Appointment is the default because it is what every existing course does.
  scheduleType: 'APPOINTMENT',
  classDays: [],
  startTime: '16:00',
  endTime: '17:00',
  timezone: localTimezone(),
  durationMode: 'ONGOING',
  durationWeeks: '8',
  // Deliberately unset, like the language: what a family is promised about
  // learning — journey only, observation, or rubric and milestones — is the
  // teacher's to say, not a default's.
  learningProfile: '',
  // Deliberately unset. A course is taught in one language or the other and
  // guessing puts a wrong answer in front of a parent choosing a class, so the
  // teacher is asked before the course can be published.
  language: '',
  overview: '',
  learningObjectives: [],
  classStructure: '',
  whatToExpect: '',
};

/**
 * TCH-06 — course authoring (story S4.3, the T0 subset).
 *
 * Scoped deliberately to the fields the listing aggregate holds today. The
 * richer offer model from ADR-007 S4.2 — offer mode, delivery format, billing
 * model, recurring class days — is Wave 3 and gated on decisions TCH-D10/D11/D12,
 * so it is not stubbed in here.
 *
 * A course is always created as DRAFT; sending it to review is a separate
 * action from the course list and requires an approved application. Since
 * UC-A08 nothing here publishes: a reviewer approves the exact version sent.
 */
const TeacherCourseForm: React.FC = () => {
  const navigate = useNavigate();
  const { courseId } = useParams<{ courseId: string }>();
  const isEdit = Boolean(courseId);

  const [form, setForm] = useState<FormState>(EMPTY);
  const [errors, setErrors] = useState<Errors>({});
  const [apiError, setApiError] = useState('');
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  /**
   * The reviewer's request, when the course came back for changes (UC-A08).
   * Kept beside the form so the teacher edits against it rather than from
   * memory of the list screen.
   */
  const [reviewRequest, setReviewRequest] = useState<string | null>(null);

  // Editing loads from the teacher's own list — there is no single-course
  // owner-scoped read endpoint, and the public one omits DRAFTs.
  useEffect(() => {
    if (!isEdit) return;
    let cancelled = false;
    (async () => {
      try {
        const { courses } = await getMyCourses();
        const course = courses.find((c) => c.listingId === courseId);
        if (cancelled) return;
        if (!course) {
          setApiError('No encontramos ese curso entre los tuyos.');
          return;
        }
        if (course.reviewStatus === 'CHANGES_REQUIRED') {
          setReviewRequest(course.reviewFeedback ?? 'El equipo de revisión pidió cambios.');
        }
        setForm({
          title: course.title,
          description: course.description,
          subject: course.category || SUBJECTS[0],
          gradeLevel: course.gradeLevel ?? [],
          duration: String(course.duration),
          maxStudents: String(course.maxStudents),
          price: String(course.price),
          offerMode: course.offerMode ?? 'GROUP',
          scheduleType: course.scheduleType ?? 'APPOINTMENT',
          classDays: course.schedule?.classDays ?? [],
          startTime: course.schedule?.startTime ?? '16:00',
          endTime: course.schedule?.endTime ?? '17:00',
          timezone: course.schedule?.timezone ?? localTimezone(),
          durationMode: course.durationMode ?? 'ONGOING',
          durationWeeks: course.durationWeeks ? String(course.durationWeeks) : '8',
          // MC-01 v1-7: only LEP_0 / LEP_1 can be chosen. A course saved with
          // no profile, or with a deferred one, starts unselected: the teacher
          // chooses, nothing is inferred for them.
          learningProfile:
            course.learningProfile && MVP_PROFILES.includes(course.learningProfile)
              ? course.learningProfile
              : '',
          language: course.language ?? '',
          overview: course.overview ?? '',
          learningObjectives: course.learningObjectives ?? [],
          classStructure: course.classStructure ?? '',
          whatToExpect: course.whatToExpect ?? '',
        });
      } catch (err: any) {
        if (!cancelled) setApiError(err?.message || 'No pudimos cargar el curso.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [courseId, isEdit]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
    setApiError('');
  };

  const toggleDay = (day: number) => {
    setForm((prev) => ({
      ...prev,
      classDays: prev.classDays.includes(day)
        ? prev.classDays.filter((d) => d !== day)
        : [...prev.classDays, day],
    }));
    setErrors((prev) => ({ ...prev, classDays: undefined }));
  };

  const toggleGrade = (grade: string) => {
    setForm((prev) => ({
      ...prev,
      gradeLevel: prev.gradeLevel.includes(grade)
        ? prev.gradeLevel.filter((g) => g !== grade)
        : [...prev.gradeLevel, grade],
    }));
    setErrors((prev) => ({ ...prev, gradeLevel: undefined }));
  };

  const setObjective = (index: number, value: string) => {
    setForm((prev) => ({
      ...prev,
      learningObjectives: prev.learningObjectives.map((o, i) => (i === index ? value : o)),
    }));
    setErrors((prev) => ({ ...prev, learningObjectives: undefined }));
  };

  const addObjective = () =>
    setForm((prev) => ({ ...prev, learningObjectives: [...prev.learningObjectives, ''] }));

  const removeObjective = (index: number) => {
    setForm((prev) => ({
      ...prev,
      learningObjectives: prev.learningObjectives.filter((_, i) => i !== index),
    }));
    setErrors((prev) => ({ ...prev, learningObjectives: undefined }));
  };

  /** Mirrors CreateListingDto so the teacher is not bounced by a server 400. */
  const validate = (): boolean => {
    const next: Errors = {};
    const title = form.title.trim();
    const description = form.description.trim();

    if (title.length < COURSE_LIMITS.titleMin) {
      next.title = `El título necesita al menos ${COURSE_LIMITS.titleMin} caracteres.`;
    } else if (title.length > COURSE_LIMITS.titleMax) {
      next.title = `El título no puede pasar de ${COURSE_LIMITS.titleMax} caracteres.`;
    }

    if (description.length < COURSE_LIMITS.descriptionMin) {
      next.description = `La descripción necesita al menos ${COURSE_LIMITS.descriptionMin} caracteres.`;
    }

    if (!form.subject) next.subject = 'Elige una materia.';
    if (!form.learningProfile) {
      next.learningProfile = 'Elige el tipo de curso: Experiencia o Práctica guiada.';
    }
    if (form.gradeLevel.length === 0) next.gradeLevel = 'Elige al menos un grado.';

    const duration = Number(form.duration);
    if (
      !Number.isFinite(duration) ||
      duration < COURSE_LIMITS.durationMin ||
      duration > COURSE_LIMITS.durationMax
    ) {
      next.duration = `Entre ${COURSE_LIMITS.durationMin} y ${COURSE_LIMITS.durationMax} minutos.`;
    }

    const maxStudents = Number(form.maxStudents);
    if (
      !Number.isInteger(maxStudents) ||
      maxStudents < COURSE_LIMITS.maxStudentsMin ||
      maxStudents > COURSE_LIMITS.maxStudentsMax
    ) {
      next.maxStudents = `Entre ${COURSE_LIMITS.maxStudentsMin} y ${COURSE_LIMITS.maxStudentsMax} alumnos.`;
    }

    const price = Number(form.price);
    if (!Number.isFinite(price) || price < COURSE_LIMITS.priceMin) {
      next.price = 'Ingresa un precio válido.';
    }

    if (form.scheduleType === 'RECURRING') {
      if (form.classDays.length === 0) {
        next.classDays = 'Elige al menos un día de clase.';
      }
      if (!/^([01]\d|2[0-3]):([0-5]\d)$/.test(form.startTime) || form.startTime >= form.endTime) {
        next.startTime = 'La hora de inicio debe ser anterior a la de fin.';
      } else {
        // The class slot has to be long enough to hold one session — the same
        // rule the server enforces, checked here so the teacher sees it inline.
        const [sh, sm] = form.startTime.split(':').map(Number);
        const [eh, em] = form.endTime.split(':').map(Number);
        const slot = eh * 60 + em - (sh * 60 + sm);
        if (Number.isFinite(duration) && slot < duration) {
          next.startTime = `La clase dura ${slot} min pero cada sesión necesita ${duration} min.`;
        }
      }
      if (!form.timezone.trim()) {
        next.timezone = 'Ingresa la zona horaria.';
      }
    }

    if (form.durationMode === 'FIXED') {
      const weeks = Number(form.durationWeeks);
      if (
        !Number.isInteger(weeks) ||
        weeks < SCHEDULE_LIMITS.minDurationWeeks ||
        weeks > SCHEDULE_LIMITS.maxDurationWeeks
      ) {
        next.durationWeeks = `Entre ${SCHEDULE_LIMITS.minDurationWeeks} y ${SCHEDULE_LIMITS.maxDurationWeeks} semanas.`;
      }
    }

    // ── Course content (S4.3). Mirrors course-content-rules.ts.
    //
    // Language is not required here: the server requires it at publish, and a
    // draft has always been saveable half-finished. Refusing the save would
    // make the teacher answer it before they have written the course.
    if (form.learningObjectives.some((o) => o.trim() === '')) {
      next.learningObjectives = 'Quita los objetivos vacíos antes de guardar.';
    } else if (form.learningObjectives.length > CONTENT_LIMITS.objectivesMax) {
      next.learningObjectives = `Máximo ${CONTENT_LIMITS.objectivesMax} objetivos.`;
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  /**
   * The content fields, omitted when empty rather than sent as ''.
   *
   * The server treats '' as a clear, which is right for an edit that removes a
   * section and wrong for a create that never had one — an empty string there
   * would store a section the course page then draws a heading over.
   */
  const contentPayload = () => {
    const trimmed = (v: string) => v.trim();
    const objectives = form.learningObjectives.map(trimmed).filter((o) => o !== '');
    return {
      ...(form.language ? { language: form.language } : {}),
      ...(trimmed(form.overview) ? { overview: trimmed(form.overview) } : {}),
      ...(objectives.length ? { learningObjectives: objectives } : {}),
      ...(trimmed(form.classStructure) ? { classStructure: trimmed(form.classStructure) } : {}),
      ...(trimmed(form.whatToExpect) ? { whatToExpect: trimmed(form.whatToExpect) } : {}),
    };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    const payload: CourseInput = {
      title: form.title.trim(),
      description: form.description.trim(),
      subject: form.subject,
      gradeLevel: form.gradeLevel,
      duration: Number(form.duration),
      // A 1-on-1 course seats one student; the server normalises this too.
      maxStudents: form.offerMode === 'ONE_ON_ONE' ? 1 : Number(form.maxStudents),
      price: Number(form.price),
      // Sent, not left to a server default. The author is shown which currency
      // they are pricing in (the field label below reads from the same
      // constant), so the number they typed and the number stored mean the
      // same thing.
      currency: PLATFORM_CURRENCY,
      offerMode: form.offerMode,
      scheduleType: form.scheduleType,
      // Omitted entirely when not recurring — the server rejects a schedule on
      // an appointment-based course rather than ignoring it.
      ...(form.scheduleType === 'RECURRING'
        ? {
            schedule: {
              classDays: form.classDays,
              startTime: form.startTime,
              endTime: form.endTime,
              timezone: form.timezone.trim(),
            },
          }
        : {}),
      durationMode: form.durationMode,
      ...(form.durationMode === 'FIXED' ? { durationWeeks: Number(form.durationWeeks) } : {}),
      ...(form.learningProfile ? { learningProfile: form.learningProfile } : {}),
      ...contentPayload(),
    };

    setSaving(true);
    setApiError('');
    try {
      if (isEdit && courseId) {
        await updateCourse(courseId, payload);
      } else {
        await createCourse(payload);
      }
      navigate('/teacher/courses', { replace: true });
    } catch (err: any) {
      setApiError(err?.message || 'No pudimos guardar el curso. Inténtalo de nuevo.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p>Cargando curso...</p>;

  return (
    <div style={{ maxWidth: 640, margin: '0 auto' }}>
      <h1 style={{ marginBottom: 4 }}>{isEdit ? 'Editar curso' : 'Crear curso'}</h1>
      <p style={{ color: '#666', marginTop: 0 }}>
        {isEdit
          ? 'Solo los borradores se pueden editar. Despublica un curso si necesitas cambiarlo.'
          : 'Se guardará como borrador. Desde “Mis cursos” podrás enviarlo a revisión; se publica cuando el equipo lo aprueba.'}
      </p>

      {reviewRequest && (
        <div
          role="status"
          style={{
            background: '#fff8e6',
            border: '1px solid #f5a623',
            color: '#7a4d06',
            borderRadius: 8,
            padding: '10px 14px',
            marginBottom: 12,
            fontSize: 14,
          }}
        >
          <strong>El equipo de revisión pide cambios:</strong> {reviewRequest}
          <div style={{ marginTop: 6 }}>
            Guarda tus cambios y luego usa “Reenviar a revisión” en “Mis cursos”.
          </div>
        </div>
      )}

      {apiError && (
        <div role="alert" style={alertStyle}>
          {apiError}
        </div>
      )}

      <form onSubmit={handleSubmit} noValidate>
        <Field label="Título del curso" error={errors.title} htmlFor="title">
          <input
            id="title"
            className="form-input"
            value={form.title}
            onChange={(e) => set('title', e.target.value)}
            placeholder="Ej. Matemáticas divertidas para 4to grado"
            maxLength={COURSE_LIMITS.titleMax}
          />
        </Field>

        <Field
          label="Descripción"
          error={errors.description}
          htmlFor="description"
          help="Explica qué aprenderá el alumno y cómo son tus clases."
        >
          <textarea
            id="description"
            className="form-input"
            value={form.description}
            onChange={(e) => set('description', e.target.value)}
            rows={5}
            style={{ resize: 'vertical' }}
          />
        </Field>

        <Field label="Materia" error={errors.subject} htmlFor="subject">
          <select
            id="subject"
            className="form-input"
            value={form.subject}
            onChange={(e) => set('subject', e.target.value)}
          >
            {SUBJECTS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </Field>

        <fieldset style={{ border: 0, padding: 0, margin: '0 0 16px' }}>
          <legend style={labelStyle}>Grados</legend>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {GRADE_LEVELS.map((g) => (
              <Pill key={g} selected={form.gradeLevel.includes(g)} onClick={() => toggleGrade(g)}>
                {GRADE_LEVEL_LABELS[g]}
              </Pill>
            ))}
          </div>
          {errors.gradeLevel && <div className="form-error">{errors.gradeLevel}</div>}
        </fieldset>

        {/* ── Course content (ADR-007 S4.3) ─────────────────────────────
            The ten fields ADR-007 names and the platform had no column for.
            Only the language is required, and only at publish — the rest are
            what a family reads on the course page, and a draft is saveable
            without them. */}
        <Field
          label="Idioma de la clase"
          error={errors.language}
          htmlFor="language"
          help="Necesario para publicar. Es lo primero que una familia busca."
        >
          <select
            id="language"
            className="form-input"
            value={form.language}
            onChange={(e) => set('language', e.target.value as CourseLanguage | '')}
          >
            <option value="">Elige un idioma…</option>
            {COURSE_LANGUAGE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </Field>

        <Field
          label="Resumen de la clase"
          error={errors.overview}
          htmlFor="overview"
          help="El párrafo que una familia lee primero."
        >
          <textarea
            id="overview"
            className="form-input"
            value={form.overview}
            onChange={(e) => set('overview', e.target.value)}
            rows={4}
            maxLength={CONTENT_LIMITS.sectionMax}
            style={{ resize: 'vertical' }}
          />
        </Field>

        <fieldset style={{ border: 0, padding: 0, margin: '0 0 16px' }}>
          <legend style={labelStyle}>Objetivos de aprendizaje</legend>
          <div className="form-help" style={{ marginBottom: 8 }}>
            Qué podrá hacer el alumno al terminar.
          </div>
          {form.learningObjectives.map((objective, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <input
                className="form-input"
                aria-label={`Objetivo ${i + 1}`}
                value={objective}
                onChange={(e) => setObjective(i, e.target.value)}
                maxLength={CONTENT_LIMITS.objectiveMax}
                style={{ flex: 1 }}
              />
              <button
                type="button"
                onClick={() => removeObjective(i)}
                aria-label={`Quitar objetivo ${i + 1}`}
                style={secondaryBtnStyle}
              >
                Quitar
              </button>
            </div>
          ))}
          {form.learningObjectives.length < CONTENT_LIMITS.objectivesMax && (
            <button type="button" onClick={addObjective} style={secondaryBtnStyle}>
              Agregar objetivo
            </button>
          )}
          {errors.learningObjectives && (
            <div className="form-error">{errors.learningObjectives}</div>
          )}
        </fieldset>

        <Field
          label="Estructura de la clase"
          error={errors.classStructure}
          htmlFor="classStructure"
          help="Cómo transcurre una sesión."
        >
          <textarea
            id="classStructure"
            className="form-input"
            value={form.classStructure}
            onChange={(e) => set('classStructure', e.target.value)}
            rows={3}
            maxLength={CONTENT_LIMITS.sectionMax}
            style={{ resize: 'vertical' }}
          />
        </Field>

        <Field
          label="Qué esperar"
          error={errors.whatToExpect}
          htmlFor="whatToExpect"
          help="Lo que necesita la familia para aprovechar la clase."
        >
          <textarea
            id="whatToExpect"
            className="form-input"
            value={form.whatToExpect}
            onChange={(e) => set('whatToExpect', e.target.value)}
            rows={3}
            maxLength={CONTENT_LIMITS.sectionMax}
            style={{ resize: 'vertical' }}
          />
        </Field>

        {/* ── Offer model (ADR-007 S4.2) ───────────────────────────────── */}
        <fieldset style={{ border: 0, padding: 0, margin: '0 0 16px' }}>
          <legend style={labelStyle}>Formato</legend>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {OFFER_MODE_OPTIONS.map((opt) => (
              <Pill
                key={opt.value}
                selected={form.offerMode === opt.value}
                onClick={() => set('offerMode', opt.value)}
              >
                {opt.label}
              </Pill>
            ))}
          </div>
          <div className="form-help">
            {OFFER_MODE_OPTIONS.find((o) => o.value === form.offerMode)?.help}
          </div>
        </fieldset>

        {/* ── Learning profile (LEM-01) ───────────────────────────────── */}
        <fieldset style={{ border: 0, padding: 0, margin: '0 0 16px' }}>
          <legend style={labelStyle}>¿Qué tipo de curso es?</legend>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {LEARNING_PROFILE_OPTIONS.map((opt) => (
              <Pill
                key={opt.value}
                selected={form.learningProfile === opt.value}
                onClick={() => set('learningProfile', opt.value)}
              >
                {opt.label}
              </Pill>
            ))}
          </div>
          {/* Both explained, always: the choice is part of what the family buys. */}
          <ul className="form-help" style={{ margin: '6px 0 0', paddingLeft: 18 }}>
            {LEARNING_PROFILE_OPTIONS.map((opt) => (
              <li key={opt.value}>
                <strong>{opt.label}:</strong> {opt.help}
              </li>
            ))}
          </ul>
          <div className="form-help">{LEARNING_PROFILE_NOTE}</div>
          {errors.learningProfile && <div className="form-error">{errors.learningProfile}</div>}
        </fieldset>

        <fieldset style={{ border: 0, padding: 0, margin: '0 0 16px' }}>
          <legend style={labelStyle}>¿Cómo se agenda?</legend>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {SCHEDULE_TYPE_OPTIONS.map((opt) => (
              <Pill
                key={opt.value}
                selected={form.scheduleType === opt.value}
                onClick={() => set('scheduleType', opt.value)}
              >
                {opt.label}
              </Pill>
            ))}
          </div>
          <div className="form-help">
            {SCHEDULE_TYPE_OPTIONS.find((o) => o.value === form.scheduleType)?.help}
          </div>
        </fieldset>

        {form.scheduleType === 'RECURRING' && (
          <div
            style={{
              border: '1px solid #d1d5da',
              borderRadius: 8,
              padding: 16,
              marginBottom: 16,
              background: '#f7f9fc',
            }}
          >
            <fieldset style={{ border: 0, padding: 0, margin: '0 0 14px' }}>
              <legend style={labelStyle}>Días de clase</legend>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {WEEK_DAYS.map((d) => (
                  <Pill
                    key={d.value}
                    selected={form.classDays.includes(d.value)}
                    onClick={() => toggleDay(d.value)}
                  >
                    {d.label}
                  </Pill>
                ))}
              </div>
              {errors.classDays && <div className="form-error">{errors.classDays}</div>}
            </fieldset>

            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 130px' }}>
                <Field label="Empieza" htmlFor="startTime" error={errors.startTime}>
                  <input
                    id="startTime"
                    className="form-input"
                    type="time"
                    value={form.startTime}
                    onChange={(e) => set('startTime', e.target.value)}
                  />
                </Field>
              </div>
              <div style={{ flex: '1 1 130px' }}>
                <Field label="Termina" htmlFor="endTime">
                  <input
                    id="endTime"
                    className="form-input"
                    type="time"
                    value={form.endTime}
                    onChange={(e) => set('endTime', e.target.value)}
                  />
                </Field>
              </div>
              <div style={{ flex: '1 1 200px' }}>
                <Field label="Zona horaria" htmlFor="timezone" error={errors.timezone}>
                  <input
                    id="timezone"
                    className="form-input"
                    value={form.timezone}
                    onChange={(e) => set('timezone', e.target.value)}
                    placeholder="America/Mexico_City"
                  />
                </Field>
              </div>
            </div>
          </div>
        )}

        <fieldset style={{ border: 0, padding: 0, margin: '0 0 16px' }}>
          <legend style={labelStyle}>Duración del curso</legend>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {DURATION_MODE_OPTIONS.map((opt) => (
              <Pill
                key={opt.value}
                selected={form.durationMode === opt.value}
                onClick={() => set('durationMode', opt.value)}
              >
                {opt.label}
              </Pill>
            ))}
          </div>
          <div className="form-help">
            {DURATION_MODE_OPTIONS.find((o) => o.value === form.durationMode)?.help}
          </div>
          {form.durationMode === 'FIXED' && (
            <div style={{ maxWidth: 200, marginTop: 12 }}>
              <Field label="Semanas" htmlFor="durationWeeks" error={errors.durationWeeks}>
                <input
                  id="durationWeeks"
                  className="form-input"
                  type="number"
                  inputMode="numeric"
                  value={form.durationWeeks}
                  onChange={(e) => set('durationWeeks', e.target.value)}
                  min={SCHEDULE_LIMITS.minDurationWeeks}
                  max={SCHEDULE_LIMITS.maxDurationWeeks}
                />
              </Field>
            </div>
          )}
        </fieldset>

        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 150px' }}>
            <Field label="Duración (min)" error={errors.duration} htmlFor="duration">
              <input
                id="duration"
                className="form-input"
                type="number"
                inputMode="numeric"
                value={form.duration}
                onChange={(e) => set('duration', e.target.value)}
                min={COURSE_LIMITS.durationMin}
                max={COURSE_LIMITS.durationMax}
              />
            </Field>
          </div>
          {form.offerMode !== 'ONE_ON_ONE' && (
            <div style={{ flex: '1 1 150px' }}>
              <Field label="Máx. alumnos" error={errors.maxStudents} htmlFor="maxStudents">
                <input
                  id="maxStudents"
                  className="form-input"
                  type="number"
                  inputMode="numeric"
                  value={form.maxStudents}
                  onChange={(e) => set('maxStudents', e.target.value)}
                  min={COURSE_LIMITS.maxStudentsMin}
                  max={COURSE_LIMITS.maxStudentsMax}
                />
              </Field>
            </div>
          )}
          <div style={{ flex: '1 1 150px' }}>
            <Field
              label={`Precio por sesión (${PLATFORM_CURRENCY})`}
              error={errors.price}
              htmlFor="price"
            >
              <input
                id="price"
                className="form-input"
                type="number"
                inputMode="decimal"
                step="0.01"
                value={form.price}
                onChange={(e) => set('price', e.target.value)}
                min={COURSE_LIMITS.priceMin}
              />
            </Field>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 12, flexWrap: 'wrap' }}>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Guardando…' : isEdit ? 'Guardar cambios' : 'Crear borrador'}
          </button>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => navigate('/teacher/courses')}
            disabled={saving}
          >
            Cancelar
          </button>
        </div>
      </form>
    </div>
  );
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 13,
  fontWeight: 600,
  color: '#444',
  marginBottom: 6,
  padding: 0,
};

/** Selectable pill, shared by grades, offer mode, schedule type and class days. */
const Pill: React.FC<{
  selected: boolean;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ selected, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={selected}
    style={{
      border: `1px solid ${selected ? '#4a90e2' : '#d1d5da'}`,
      background: selected ? '#eaf2fb' : '#fff',
      color: selected ? '#1c4f80' : '#444',
      fontWeight: selected ? 700 : 500,
      borderRadius: 999,
      padding: '6px 14px',
      cursor: 'pointer',
      fontSize: 13,
    }}
  >
    {children}
  </button>
);

const Field: React.FC<{
  label: string;
  htmlFor: string;
  error?: string;
  help?: string;
  children: React.ReactNode;
}> = ({ label, htmlFor, error, help, children }) => (
  <div className="form-group" style={{ marginBottom: 16 }}>
    <label htmlFor={htmlFor} style={labelStyle}>
      {label}
    </label>
    {children}
    {help && !error && <div className="form-help">{help}</div>}
    {error && <div className="form-error">{error}</div>}
  </div>
);

/** Low-emphasis action inside a fieldset — add or remove a repeated row. */
const secondaryBtnStyle: React.CSSProperties = {
  border: '1px solid #d1d5da',
  background: '#fff',
  color: '#444',
  borderRadius: 6,
  padding: '6px 12px',
  cursor: 'pointer',
  fontSize: 13,
};

const alertStyle: React.CSSProperties = {
  background: '#ffe9e9',
  border: '1px solid #cb4242',
  color: '#8f2323',
  borderRadius: 8,
  padding: '10px 14px',
  marginBottom: 16,
  fontSize: 14,
};

export default TeacherCourseForm;
